import { useLocation } from 'react-router-dom';
import { LayoutDashboard, Puzzle, Settings, User, ChevronRight, HelpCircle, LucideIcon } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SidebarItem {
  name: string;
  icon: LucideIcon;
  path: string;
  description: string;
  badge?: string;
}

// Sidebar links with sections
const sidebarLinks: Array<{ section: string; items: SidebarItem[] }> = [
  {
    section: 'Principal',
    items: [
      { 
        name: 'Dashboard', 
        icon: LayoutDashboard, 
        path: '/dashboard',
        description: 'Vista general y métricas'
      },
    ]
  },
  {
    section: 'Gestión',
    items: [
      { 
        name: 'MVPs', 
        icon: Puzzle, 
        path: '/mvps',
        description: 'Administrar MVPs'
      },
      { 
        name: 'Usuarios', 
        icon: User, 
        path: '/users',
        description: 'Gestión de usuarios'
      },
    ]
  },
  {
    section: 'Configuración',
    items: [
      {
        name: 'Ajustes',
        icon: Settings,
        path: '/settings',
        description: 'Configuración general'
      },
      {
        name: 'Ayuda',
        icon: HelpCircle,
        path: '/help',
        description: 'Centro de ayuda'
      }
    ]
  }
];

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();

  // Sidebar active link
  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <aside 
        className={`
          fixed md:static z-40 top-0 left-0 h-screen w-72 md:w-64 
          bg-gradient-to-b from-slate-800 to-slate-900
          text-white flex flex-col
          transition-transform duration-300 shadow-xl md:shadow-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Mobile close button */}
        <button
          className="md:hidden absolute top-4 right-4 p-2 hover:bg-white/5 rounded-lg transition-colors"
          onClick={onClose}
        >
          <ChevronRight className="h-5 w-5 text-slate-300" />
        </button>

        {/* Logo section */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <svg className="h-8 w-8 text-primary-400" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="16" fill="currentColor" />
                <path d="M10 17l4-4 4 4 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Validea</h1>
              <p className="text-xs text-slate-400">Validación de MVPs</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto">
          {sidebarLinks.map((section) => (
            <div key={section.section} className="mb-6 last:mb-0">
              <h2 className="px-6 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {section.section}
              </h2>
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <li key={item.name}>
                      <a
                        href={item.path}
                        className={`
                          group relative flex items-center px-6 py-2
                          text-sm font-medium
                          transition-colors
                          ${active 
                            ? 'text-white bg-white/10' 
                            : 'text-slate-300 hover:text-white hover:bg-white/5'
                          }
                        `}
                      >
                        <item.icon className="h-5 w-5 mr-3" />
                        <span>{item.name}</span>
                        {item.badge && (
                          <span className={`
                            ml-auto px-2 py-0.5 text-xs rounded-full
                            ${item.badge === 'New' 
                              ? 'bg-primary-500/20 text-primary-400'
                              : 'bg-white/10 text-slate-300'
                            }
                          `}>
                            {item.badge}
                          </span>
                        )}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-30"
          onClick={onClose}
        />
      )}
    </>
  );
} 